import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Card, CardBody, CardHeader, Col, Container, Nav, NavItem, NavLink, Row, TabContent, TabPane, Table,
    Button, Spinner, Alert,
} from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import api, { handleApiError } from "../../../../helpers/api";
import { toast } from "react-toastify";

const customStyles = `
  .profile-header {
    background: #e6f7f6;
    color: #2a4966;
    border-radius: 8px;
    padding: 2rem;
    text-align: center;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(10, 179, 156, 0.2);
  }
  .profile-header h3 {
    margin-bottom: 0.5rem;
    font-size: 1.8rem;
    font-weight: 600;
    color: #2a4966;
  }
  .profile-header p {
    margin-bottom: 0.3rem;
    font-size: 1rem;
    color: #405189;
  }
  .nav-pills .nav-link {
    border-radius: 0.25rem;
    margin-right: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-weight: 500;
  }
  .nav-pills .nav-link.active {
    background-color: #405189;
    color: white;
  }
  .cv-iframe-container {
    position: relative;
    width: 100%;
    height: 600px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    background-color: #f8f9fa;
  }
  .cv-iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
  .cv-loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  @media (max-width: 768px) {
    .profile-header {
      padding: 1.5rem;
    }
    .profile-header h3 {
      font-size: 1.5rem;
    }
    .cv-iframe-container {
      height: 400px;
    }
  }
`;

const SimplePage = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();
    const candidate = state?.candidate;
    const job = state?.job;
    console.log("Candidate:", candidate);
    const [isLoading, setIsLoading] = useState(true);
    const [Candidate, setCandidate] = useState(candidate || {});
    const [Job, setJob] = useState({
        titre: job?.jobTitle || t("candidate_profile.na"),
        position: job?.jobPosition || t("candidate_profile.na"),
        category: job?.jobCategory || t("candidate_profile.na"),
        type: job?.jobType || t("candidate_profile.na"),
        country: job?.country || "",
        state: job?.state || "",
    });
    const [activeTab, setActiveTab] = useState("1");
    const [isCvLoading, setIsCvLoading] = useState(true);
    const [cvError, setCvError] = useState(null);
    const [cvObjectUrl, setCvObjectUrl] = useState(null);

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const isVpsUrl = (url) => url?.startsWith("https://files.confidoo.com.tn/private/job-submissions/");

    const getFilenameFromUrl = (url) => {
        if (!url) return null;
        return url.split("/").pop();
    };

    useEffect(() => {
        if (!candidate || !job) {
            toast.error(t("candidate_profile.error_missing_data"));
            navigate("/apps-job-candidate-lists");
        } else {
            setIsLoading(false);
        }
    }, [candidate, job, navigate, t]);

    useEffect(() => {
        const fetchCv = async () => {
            if (!Candidate?.cv) {
                setIsCvLoading(false);
                setCvError(t("candidate_profile.cv_error_missing"));
                return;
            }

            setIsCvLoading(true);
            setCvError(null);

            if (isVpsUrl(Candidate.cv)) {
                const filename = getFilenameFromUrl(Candidate.cv);
                try {
                    const response = await api.get(`/carriere/cvs/${filename}`, {
                        responseType: "blob",
                    });

                    if (response.data.type !== "application/pdf") {
                        throw new Error("Invalid file type");
                    }

                    const objectUrl = URL.createObjectURL(response.data);
                    setCvObjectUrl(objectUrl);
                    setIsCvLoading(false);

                    return () => URL.revokeObjectURL(objectUrl);
                } catch (error) {
                    setIsCvLoading(false);
                    handleApiError(error, () => {}, () => {});
                    setCvError(
                        error.response?.status === 404
                            ? t("candidate_profile.cv_error_not_found")
                            : error.response?.status === 403
                                ? t("candidate_profile.cv_error_unauthorized")
                                : t("candidate_profile.cv_error_network")
                    );
                }
            } else {
                fetch(Candidate.cv, { method: "HEAD" })
                    .then((response) => {
                        if (response.ok && response.headers.get("content-type")?.includes("application/pdf")) {
                            setIsCvLoading(false);
                        } else {
                            setIsCvLoading(false);
                            setCvError(t("candidate_profile.cv_error_invalid"));
                        }
                    })
                    .catch(() => {
                        setIsCvLoading(false);
                        setCvError(t("candidate_profile.cv_error_network"));
                    });
            }
        };

        fetchCv();
    }, [Candidate?.cv, t]);

    if (isLoading) {
        return (
            <div className="text-center py-4">
                <Spinner color="primary" />
            </div>
        );
    }

    document.title = `Candidate Profile - ${Candidate?.nom || "Default Name"}`;

    return (
        <React.Fragment>
            <style>{customStyles}</style>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        title={t("candidate_profile.title", { name: Candidate?.nom || t("candidate_profile.default_name") })}
                        pageTitle={t("candidate_profile.page_title")}
                    />
                    <Row className="mb-4">
                        <Col>
                            <Card className="profile-header">
                                <CardBody>
                                    <h3>{Candidate?.nom || t("candidate_profile.default_name")}</h3>
                                    <p>
                                        <i className="ri-mail-line me-1"></i>
                                        {Candidate?.email || t("candidate_profile.na")}
                                    </p>
                                    <p>
                                        <i className="ri-briefcase-2-line me-1"></i>
                                        {`Applied for: ${Job?.titre || t("candidate_profile.na")}`}
                                    </p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <Nav pills className="nav-justified">
                                        <NavItem>
                                            <NavLink
                                                className={classnames({ active: activeTab === "1" })}
                                                onClick={() => toggleTab("1")}
                                            >
                                                <i className="ri-user-3-line me-1 align-bottom"></i>
                                                {t("candidate_profile.tabs.overview")}
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                className={classnames({ active: activeTab === "2" })}
                                                onClick={() => toggleTab("2")}
                                            >
                                                <i className="ri-file-text-line me-1 align-bottom"></i>
                                                {t("candidate_profile.tabs.cv_view")}
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                </CardHeader>
                                <CardBody>
                                    <TabContent activeTab={activeTab}>
                                        <TabPane tabId="1">
                                            <Row>
                                                <Col xxl={4} md={6}>
                                                    <Card>
                                                        <CardBody>
                                                            <h5 className="card-title mb-3">{t("candidate_profile.overview.candidate_info")}</h5>
                                                            <Table className="table-borderless mb-0">
                                                                <tbody>
                                                                <tr>
                                                                    <th className="ps-0" scope="row">
                                                                        {t("candidate_profile.overview.full_name")}:
                                                                    </th>
                                                                    <td className="text-muted">{Candidate?.nom || t("candidate_profile.na")}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th className="ps-0" scope="row">
                                                                        {t("candidate_profile.overview.email")}:
                                                                    </th>
                                                                    <td className="text-muted">{Candidate?.email || t("candidate_profile.na")}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th className="ps-0" scope="row">
                                                                        {t("candidate_profile.overview.job_applied")}:
                                                                    </th>
                                                                    <td className="text-muted">{Job?.titre || t("candidate_profile.na")}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th className="ps-0" scope="row">
                                                                        {t("candidate_profile.overview.message")}:
                                                                    </th>
                                                                    <td className="text-muted">{Candidate?.message || t("candidate_profile.overview.message_none")}</td>
                                                                </tr>
                                                                </tbody>
                                                            </Table>
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                                <Col xxl={8} md={6}>
                                                    <Card>
                                                        <CardBody>
                                                            <h5 className="card-title mb-3">{t("candidate_profile.overview.application_details")}</h5>
                                                            <p>{Candidate?.message || t("candidate_profile.overview.message_none")}</p>
                                                            {Job && Object.keys(Job).length > 0 ? (
                                                                <div className="mt-3">
                                                                    <h6>{t("candidate_profile.overview.job_details")}</h6>
                                                                    <p>
                                                                        <strong>{t("candidate_profile.overview.position")}:</strong>{" "}
                                                                        {Job.position || t("candidate_profile.na")}<br />
                                                                        <strong>{t("candidate_profile.overview.category")}:</strong>{" "}
                                                                        {Job.category || t("candidate_profile.na")}<br />
                                                                        <strong>{t("candidate_profile.overview.type")}:</strong>{" "}
                                                                        {Job.type || t("candidate_profile.na")}<br />
                                                                        <strong>{t("candidate_profile.overview.location")}:</strong>{" "}
                                                                        {Job.country && Job.state
                                                                            ? `${Job.country}, ${Job.state}`
                                                                            : t("candidate_profile.na")}
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                <p>{t("candidate_profile.overview.job_details_unavailable")}</p>
                                                            )}
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        </TabPane>
                                        <TabPane tabId="2">
                                            <Card>
                                                <CardBody>
                                                    <h5 className="card-title mb-3">{t("candidate_profile.cv_preview.title")}</h5>
                                                    {cvError ? (
                                                        <Alert color="danger">{cvError}</Alert>
                                                    ) : (
                                                        <div className="cv-iframe-container">
                                                            {isCvLoading && <Spinner className="cv-loading" color="primary" />}
                                                            {Candidate?.cv && !isCvLoading && (
                                                                <embed
                                                                    src={isVpsUrl(Candidate.cv) ? cvObjectUrl : Candidate.cv}
                                                                    title={t("candidate_profile.cv_preview.embed_title")}
                                                                    className="cv-iframe"
                                                                    type="application/pdf"
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </TabPane>
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default SimplePage;